import type { PatientStepId, PaymentTiming, WorkflowTemplateId } from "../contracts"

export type WorkflowTemplate = {
	readonly id: WorkflowTemplateId
	readonly version: number
	readonly intakeSteps: readonly PatientStepId[]
	readonly policies: {
		readonly shipKitPaymentTiming: PaymentTiming
		readonly uploadedLabsPaymentTiming: "after_lab_results"
	}
}

const SHARED_INTAKE_STEPS = [
	"questionnaire",
	"account",
	"gender",
	"eligibility-screening",
	"post-auth",
	"shipping",
	"whats-next",
	"membership-agreement",
	"testosterone-agreement",
	"selection",
	"photo-id",
	"lab-results",
] as const satisfies readonly PatientStepId[]

export const DEFAULT_WORKFLOW_TEMPLATE = {
	id: "default",
	version: 1,
	intakeSteps: SHARED_INTAKE_STEPS,
	policies: {
		shipKitPaymentTiming: "before_lab_order",
		uploadedLabsPaymentTiming: "after_lab_results",
	},
} as const satisfies WorkflowTemplate

export const DEFERRED_LABS_WORKFLOW_TEMPLATE = {
	id: "deferred-labs",
	version: 1,
	intakeSteps: SHARED_INTAKE_STEPS,
	policies: {
		shipKitPaymentTiming: "after_lab_results",
		uploadedLabsPaymentTiming: "after_lab_results",
	},
} as const satisfies WorkflowTemplate

export const WORKFLOW_TEMPLATE_VERSIONS = [
	DEFAULT_WORKFLOW_TEMPLATE,
	DEFERRED_LABS_WORKFLOW_TEMPLATE,
] as const satisfies readonly WorkflowTemplate[]

export const WORKFLOW_TEMPLATES: Readonly<Record<WorkflowTemplateId, WorkflowTemplate>> = {
	default: DEFAULT_WORKFLOW_TEMPLATE,
	"deferred-labs": DEFERRED_LABS_WORKFLOW_TEMPLATE,
}

const workflowTemplateByVersion = new Map<string, WorkflowTemplate>(
	WORKFLOW_TEMPLATE_VERSIONS.map((template) => [
		`${template.id}@${template.version}`,
		template,
	] as const),
)

export function getWorkflowTemplate(
	id: WorkflowTemplateId,
	version: number = WORKFLOW_TEMPLATES[id].version,
): WorkflowTemplate {
	const template = workflowTemplateByVersion.get(`${id}@${version}`)
	if (template === undefined) {
		throw new Error(`Workflow template is not registered: ${id}@${version}`)
	}
	return template
}
